/* eslint-disable @typescript-eslint/no-unused-vars */
import { Edit, NoteAdd, KeyboardArrowDown, EditOff } from '@mui/icons-material';
import {
    Button,
    useTheme,
    useMediaQuery,
    Menu,
    MenuItem,
    Tooltip,
    Badge,
    Box,
} from '@mui/material';
import {
    DataGridProps,
    GridCsvGetRowsToExportParams,
    GridInputSelectionModel,
    GridSelectionModel,
    GridToolbarContainer,
    GridToolbarExport,
    GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import {
    DefaultOpenModalProps,
    OpenModalButton,
} from 'components/button/OpenModalButton/OpenModalButton';
import { useUi } from 'hooks/useUi';
import { useRef, useState, Fragment, useEffect } from 'react';
import { UserSessionKeys } from 'types/User/firebaseTypes';
import {
    GridToolbarColumnsButton,
    GridToolbarColumnsButtonProps,
} from './components/GridToolbarColumnsButton/GridToolbarColumnsButton';
import {
    GridToolbarExportButton,
    GridToolbarExportButtonProps,
} from './components/GridToolbarExportButton/GridToolbarExportButton';
import { formatCsvFileName } from './components/GridToolbarExportButton/helpers';

import {
    DataTableReportPreset,
    GridToolBarPresetSelector,
    GridToolBarPresetSelectorProps,
} from './components/GridToolbarPresetSelector/GridToolbarPresetSelector';
import { DataTableColumn } from './DataTable';
import styles from './DataTable.module.scss';
import { BulkEditDevicesModalButtonProps } from 'views/LiveMonitoring/components/BulkEditDevicesModal/BulkEditDevicesModal';

export interface DataTableToolBarProps extends DataGridProps {
    /** column id for our internal sorting and visibility models. Must be provided for them to work properly with local storage and user session data. */
    gridId: UserSessionKeys;
    checkboxSelectionEnabled: boolean;
    setCheckboxSelectionEnabled: React.Dispatch<React.SetStateAction<boolean>>;
    currentPreset: DataTableReportPreset | null;
    setCurrentPreset: GridToolBarPresetSelectorProps['setCurrentPreset'];
    toolbarComponents: React.ReactNode;
    columns: DataTableColumn[];
    bulkEditModal?: (props: BulkEditDevicesModalButtonProps) => JSX.Element;
    columnVisibilityModel: GridToolbarColumnsButtonProps['columnVisibilityModel'];
    setColumnVisibilityModel: GridToolbarColumnsButtonProps['setColumnVisibilityModel'];
    columnSortingOrder: GridToolbarColumnsButtonProps['columnSortingOrder'];
    setColumnSortingOrder: GridToolbarColumnsButtonProps['setColumnSortingOrder'];
    defaultColumnSortingOrder?: GridToolbarColumnsButtonProps['defaultColumnSortingOrder'];
    presets?: GridToolBarPresetSelectorProps['presets'];
    hideToolbar?: boolean;
    hideSearch?: boolean;
    csvOptions?: GridToolbarExportButtonProps['csvOptions'];
    /** Optional parameter to autofocus the search bar on the MUI Data Grid toolbar. This can be useful when server mode is enabled for search/sort/filter which causes the grid to rerender and the search bar to lose focus while user is still typing. */
    autoFocusSearch?: boolean;
    selectionModel: GridSelectionModel;
    setSelectionModel: React.Dispatch<
        React.SetStateAction<GridInputSelectionModel>
    >;
}

export function DataTableToolBar({
    gridId,
    checkboxSelectionEnabled,
    setCheckboxSelectionEnabled,
    toolbarComponents,
    presets,
    bulkEditModal,
    setCurrentPreset,
    currentPreset,
    columnVisibilityModel,
    setColumnVisibilityModel,
    columnSortingOrder,
    setColumnSortingOrder,
    defaultColumnSortingOrder,
    columns,
    hideToolbar,
    hideSearch,
    csvOptions,
    autoFocusSearch,
    selectionModel,
    setSelectionModel,
    ...props
}: DataTableToolBarProps) {
    const { ui } = useUi();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const BulkEditModal = bulkEditModal;

    const [open, setOpen] = useState(false);
    const toolbarMenuButtonRef = useRef<HTMLButtonElement>(null);

    const components = [
        <GridToolbarColumnsButton
            key={'GridToolbarColumnsButton'}
            gridId={gridId}
            columnVisibilityModel={columnVisibilityModel}
            setColumnVisibilityModel={setColumnVisibilityModel}
            columnSortingOrder={columnSortingOrder}
            setColumnSortingOrder={setColumnSortingOrder}
            defaultColumnSortingOrder={defaultColumnSortingOrder}
            columns={columns}
            currentPreset={currentPreset}
        />,
        <Fragment key="ToolBarExportButton">
            {csvOptions?.query ? (
                <GridToolbarExportButton
                    {...props}
                    gridId={gridId}
                    columns={columns}
                    columnVisibilityModel={columnVisibilityModel}
                    csvOptions={csvOptions}
                />
            ) : (
                <GridToolbarExport
                    {...props}
                    csvOptions={{
                        ...csvOptions,
                        getRowsToExport: (
                            params: GridCsvGetRowsToExportParams
                        ) => params.apiRef.current.getAllRowIds(),
                        fileName: formatCsvFileName(
                            csvOptions?.fileName ?? gridId
                        ),
                    }}
                />
            )}
        </Fragment>,
        presets ? (
            <Fragment key={'ToolBarPresetSelector'}>
                <GridToolBarPresetSelector
                    currentPreset={currentPreset}
                    presets={presets}
                    setCurrentPreset={setCurrentPreset}
                />
            </Fragment>
        ) : null,
        bulkEditModal ? (
            <Fragment key={'BulkEditButton'}>
                <Button
                    size="small"
                    startIcon={
                        checkboxSelectionEnabled ? <EditOff /> : <Edit />
                    }
                    variant={checkboxSelectionEnabled ? 'text' : 'text'}
                    onClick={() => {
                        setSelectionModel([]);
                        setCheckboxSelectionEnabled(!checkboxSelectionEnabled);
                    }}
                >
                    Bulk Edit
                </Button>
            </Fragment>
        ) : null,
    ];

    const [columnError, setColumnError] = useState(false);

    // If all columns are turned off, show a warning because the table will look really broken.
    useEffect(() => {
        setColumnError(
            Object.values(columnVisibilityModel).filter((item) => item === true)
                .length === 0
        );
    }, [columnVisibilityModel]);

    return (
        <div
            className={`${styles.toolbarContainer}  ${
                ui.isMobile ? styles.isMobile : ''
            }`}
        >
            <GridToolbarContainer className={styles.mainToolbar}>
                {!hideToolbar &&
                    (isMobile ? (
                        <div className={styles.toolbarComponents}>
                            <Tooltip
                                title={
                                    columnError
                                        ? 'No columns are set to be visible, so the table may not be showing any data. To fix the table, turn on at least one column.'
                                        : null
                                }
                            >
                                <Button
                                    id="basic-button"
                                    aria-controls={
                                        open ? 'basic-menu' : undefined
                                    }
                                    aria-haspopup="true"
                                    aria-expanded={open ? 'true' : undefined}
                                    variant="contained"
                                    onClick={() => {
                                        setOpen(!open);
                                    }}
                                    endIcon={<KeyboardArrowDown />}
                                    ref={toolbarMenuButtonRef}
                                >
                                    <Badge
                                        badgeContent={
                                            columnError ? (
                                                <span
                                                    className={
                                                        styles.noColumnWarning
                                                    }
                                                >
                                                    !
                                                </span>
                                            ) : null
                                        }
                                        color="error"
                                    >
                                        <Box sx={{ paddingRight: '10px' }}>
                                            Settings
                                        </Box>
                                    </Badge>
                                </Button>
                            </Tooltip>
                            <Menu
                                onClose={() => setOpen(!open)}
                                open={open}
                                anchorEl={toolbarMenuButtonRef.current}
                            >
                                {components
                                    .filter((C) => C)
                                    .map((C, index) => (
                                        <MenuItem
                                            key={`grid-toolbar-item-${index}`}
                                            className={styles.toolbarMenuItem}
                                        >
                                            {C}
                                        </MenuItem>
                                    ))}
                            </Menu>
                        </div>
                    ) : (
                        components
                    ))}
                {!hideToolbar && (
                    <Fragment key={'bulk-edit-modal'}>
                        <div className={styles.toolbarControls}>
                            {BulkEditModal && checkboxSelectionEnabled && (
                                <BulkEditModal
                                    selectionModel={selectionModel}
                                />
                            )}
                        </div>
                        {!hideSearch && (
                            <GridToolbarQuickFilter
                                autoFocus={autoFocusSearch}
                                className={styles.quickFilter}
                            />
                        )}
                    </Fragment>
                )}
            </GridToolbarContainer>
            <div
                className={`${styles.toolbarComponents} ${
                    ui.isMobile ? styles.isMobile : ''
                }`}
            >
                {toolbarComponents}
            </div>
        </div>
    );
}
